import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert as AlertData,
  AlertsListQuery
} from '@techgitter/contracts';
import { Box, Typography } from '@mui/material';
import { listAlerts } from '../services/alertsService';
import { AlertsFilterBar } from '../components/alerts/AlertsFilterBar';
import { AlertsTable } from '../components/alerts/AlertsTable';
import { AlertDetailDrawer } from '../components/alerts/AlertDetailDrawer';
import { BulkActionBar } from '../components/alerts/BulkActionBar';

export const AlertsPage: React.FC = () => {
  const getTodayString = () => {
    const now = new Date();
    const sydneyOffset = 11 * 60;
    const localOffset = now.getTimezoneOffset();
    const sydneyTime = new Date(now.getTime() + (sydneyOffset + localOffset) * 60000);
    return sydneyTime.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState<AlertsListQuery>({
    createdFrom: getTodayString(),
    active: true,
    pageSize: 50
  });
  
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<AlertData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadAlerts = useCallback(async (query: AlertsListQuery) => {
    setLoading(true);
    setError(null);
    try {
      const response = await listAlerts(query);
      setAlerts(response.items);
      setNextCursor(response.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts(filters);
  }, [filters, loadAlerts]);

  const handleFiltersChange = (newFilters: AlertsListQuery) => {
    setFilters(newFilters);
    setCursorHistory([]);
    setSelectedIds([]);
  };

  const handleRefresh = () => {
    loadAlerts(filters);
    setSelectedIds([]);
  };

  const handleNextPage = () => {
    if (nextCursor) {
      setCursorHistory([...cursorHistory, filters.cursor || '']);
      setFilters({ ...filters, cursor: nextCursor });
      setSelectedIds([]);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const prevCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setFilters({ ...filters, cursor: prevCursor || undefined });
      setSelectedIds([]);
    }
  };

  const handleRowClick = (alert: AlertData) => {
    setSelectedAlert(alert);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedAlert(null);
  };

  const handleUpdate = () => {
    handleRefresh();
  };

  if (error) {
    return (
      <Box sx={{ padding: 4 }}>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <AlertsFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onRefresh={handleRefresh}
      />

      <Box sx={{ marginTop: 2 }}>
        <AlertsTable
          alerts={alerts}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={handleRowClick}
          nextCursor={nextCursor}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          hasPreviousPage={cursorHistory.length > 0}
          loading={loading}
        />
      </Box>

      <AlertDetailDrawer
        alert={selectedAlert}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onUpdate={handleUpdate}
      />

      <BulkActionBar
        selectedCount={selectedIds.length}
        selectedIds={selectedIds}
        onClearSelection={() => setSelectedIds([])}
        onUpdate={handleUpdate}
      />
    </Box>
  );
};
